/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import deepEqual from 'fast-deep-equal';
import { Grid, GridColumn, textCellRenderer } from '@veeam/components';
import { GRID_HEIGHT_MODE } from '@veeam/components/lib/Grid/types';

import type { GRID_HEIGHT, CellRendererProps, RowContainerRendererInjectProps } from '@veeam/components/lib/Grid/types';
import type { IconType } from '@veeam/components';

import { CellStylesReset } from './components/CellStylesReset/CellStylesReset';
import { CustomGridRowContainer } from './components/CustomGridRowContainer/CustomGridRowContainer';

interface SelectionHandlerProps<ItemT> {
    /**
     * Array of selected rows
     */
    selected: ItemT[];
    /**
     * Current selected row
     */
    current: {
        rowIndex: number;
        rowData: ItemT;
    };
}

interface RowHandlerProps<ItemT> {
    /**
     * Row index
     */
    rowIndex: number;
    /**
     * Row data
     */
    dataItem: ItemT;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum SELECTION_MODE {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    single = 'single',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    multi = 'multi',
}

export interface ListClickEventProps<ValueT> {
    /**
     * Row index
     */
    rowIndex: number;
    /**
     * Row data
     */
    dataItem: ValueT;
}

export interface DataListProps<ItemT extends Record<string, unknown>, ValueT> {
    /**
     * Selected collection of rows
     */
    selected?: ValueT[];
    /**
     * Available rows for view and select
     */
    data: ItemT[];
    /**
     * Getter data of row
     */
    valueGetter: (option: ItemT) => ValueT;
    /**
     * Getter icon of row data
     */
    iconGetter?: (option: ItemT) => IconType | null;
    /**
     * Getter text of row data
     */
    textGetter?: (option: ItemT) => string;
    /**
     * Function for comparing two values
     */
    comparator?: (value1?: ValueT, value2?: ValueT) => boolean;
    /**
     * List disable state
     */
    disabled?: boolean;
    /**
     * Selection mode. This mode has 2 variants - `SELECTION_MODE.single` and `SELECTION_MODE.multi`
     */
    selectionMode?: SELECTION_MODE;
    /**
     * Is checkbox in rows uses
     */
    hasCheckbox?: boolean;
    /**
     * List height mode. Height has 3 mode - as number, `GRID_HEIGHT_MODE.auto` and `GRID_HEIGHT_MODE.native`
     *
     * as number - fixed size grid <br/>
     * `GRID_HEIGHT_MODE.auto` - grid size is set to the parent size <br/>
     * `GRID_HEIGHT_MODE.native` - grid size is calculated from the numbers of rows and header size
     */
    heightMode?: GRID_HEIGHT;

    itemHeight?: number | string;

    /**
     * Handle click on row
     */
    onClick?(props: ListClickEventProps<ItemT>): void;

    /**
     * Handle double click on row
     */
    onDoubleClick?(props: ListClickEventProps<ItemT>): void;

    /**
     * Handle change array of selected rows
     */
    onChange?(props: ValueT[]): void;

    /**
     * Texts settings
     */
    texts?: {
        /**
         * Text for Grid without data
         */
        empty?: string;
    };

    /**
     * Cell renderer
     */
    cellRenderer?(props: CellRendererProps<ItemT>): JSX.Element | null;
}

const defaultIconGetter = <ItemT extends Record<string, unknown>/*, ValueT */>(option: ItemT) => option &&
    (typeof option['icon'] === 'string' ||
        React.isValidElement(option['icon'] as React.ReactElement))
    ? (option['icon'] as IconType)
    : null;

const defaultTextGetter = <ItemT extends Record<string, unknown>/*, ValueT */>(option: ItemT) => {
    if (option && typeof option['text'] === 'string') {
        return option['text'];
    }

    console.warn(
        'Text not found in \'data\' or \'selected\' props. Please describe the prop \'textGetter\' in \'List\'.',
    );

    return '';
};

export const getIndexData = <ItemT extends Record<string, unknown>, ValueT>(
    data: ItemT[],
    value: ValueT,
    valueGetter: (option: ItemT) => ValueT,
    compareValue: (value1: ValueT, value2: ValueT) => boolean,
): number => {
    let result = -1;

    data.some((dataRow, idx) => {
        if (compareValue(valueGetter(dataRow), value)) {
            result = idx;

            return true;
        }

        return false;
    });

    return result;
};

/**
 * Component allows to display data in a single column on the client side with the ability to select parameters.
 */
export const DataList = <ItemT extends Record<string, unknown>, ValueT>({
    data,
    selected = [],
    valueGetter,
    iconGetter = defaultIconGetter,
    textGetter = defaultTextGetter,
    comparator = deepEqual,

    disabled = false,
    selectionMode = SELECTION_MODE.single,
    hasCheckbox = false,
    heightMode = GRID_HEIGHT_MODE.auto,
    itemHeight,
    onChange,
    onClick,
    onDoubleClick,
    texts = {
        empty: 'No data',
    },
    cellRenderer,
}: DataListProps<ItemT, ValueT>) => {
    if (selectionMode === SELECTION_MODE.single && selected.length > 1) {
        console.warn(
            'The prop \'value\' must be an array of length equal to 1 in \'List\' with selectionMode = SELECTION_MODE.single.',
        );
    }

    const getGridRowItem = (option: ItemT) => ({
        value: valueGetter(option),
        icon: iconGetter(option),
        text: textGetter(option),
    });
    const gridData = data.map(getGridRowItem);
    const gridSelected = selected.map((value) => {
        const index = getIndexData(data, value, valueGetter, comparator);
        return getGridRowItem(data[index]);
    });
    const handleChange = ({ selected }: SelectionHandlerProps<{ value: ValueT; }>) => {
        onChange?.(
            selected.map(selectedRowProp => selectedRowProp.value),
        );
    };
    const handleClick = <ItemT extends Record<string, unknown>/*, ValueT */>({ rowIndex }: RowHandlerProps<ItemT>) => {
        onClick?.({
            dataItem: data[rowIndex],
            rowIndex,
        });
    };
    const handleDoubleClick = <ItemT extends Record<string, unknown>/*, ValueT */>({ rowIndex }: RowHandlerProps<ItemT>) => {
        onDoubleClick?.({
            dataItem: data[rowIndex],
            rowIndex,
        });
    };
    const customCellRenderer = (props: CellRendererProps<ItemT>) => (
        <CellStylesReset>
            {cellRenderer(props)}
        </CellStylesReset>
    );
    const customGridRowContainerRenderer = (
        { selected, rowDisabled, gridDisabled, children, rowIndex }: RowContainerRendererInjectProps<{ value: ValueT; }>
    ) => (
        <CustomGridRowContainer selected={selected} disabled={rowDisabled} gridDisabled={gridDisabled} isLast={rowIndex === (gridData.length - 1)}>
            {children}
        </CustomGridRowContainer>
    );

    return (
        <Grid
            disabled={disabled}
            hideHeader
            data={gridData}
            height={heightMode}
            onSelectionChange={handleChange}
            onRowClick={handleClick}
            onRowDoubleClick={onDoubleClick ? handleDoubleClick : undefined}
            selection={{
                multiple: selectionMode === SELECTION_MODE.multi,
                checkbox: hasCheckbox,
                fullLength: gridData.length,
                field: 'value',
                selected: gridSelected,
            }}
            texts={texts}
            rowHeight={itemHeight}
            rowContainerRenderer={customGridRowContainerRenderer}
        >
            {<GridColumn cell={cellRenderer ? customCellRenderer : textCellRenderer} field={'text'} />}
        </Grid>
    );
};

DataList.displayName = 'DataList';
